// src/design-system/components/richTextEditor/extensions/ResizableMedia.ts
import React from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
} from "@/utils/constants/mediaUploadConstants";

// Extend Commands interface
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    resizableMedia: {
      setMedia: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        height?: number;
        mediaType?: "image" | "video" | "document";
      }) => ReturnType;
    };
  }
}

// Extended extensions for type checking
const imageExtensions: string[] = [...IMAGE_EXTENSIONS, "gif", "svg", "bmp", "ico", "tiff", "tif", "webp"];
const videoExtensions: string[] = [...VIDEO_EXTENSIONS, "mkv", "flv", "wmv"];
const documentExtensions: string[] = [...DOCUMENT_EXTENSIONS];

// Helper to detect media type from URL or extension
export const detectMediaType = (
  src: string
): "image" | "video" | "document" | "unknown" => {
  if (!src) return "unknown";

  const lowerSrc = src.toLowerCase();

  // Check for data URLs
  if (lowerSrc.startsWith("data:image/")) return "image";
  if (lowerSrc.startsWith("data:video/")) return "video";
  if (lowerSrc.startsWith("data:application/")) return "document";

  // Extract extension from URL
  try {
    const url = new URL(src, window.location.origin);
    const pathname = url.pathname.toLowerCase();
    const ext = pathname.split(".").pop() || "";

    if (imageExtensions.includes(ext)) {
      return "image";
    }
    if (videoExtensions.includes(ext)) {
      return "video";
    }
    if (documentExtensions.includes(ext)) {
      return "document";
    }
  } catch {
    // URL parsing failed, try simple extension check
    const ext = src.split(".").pop()?.toLowerCase() || "";
    if (imageExtensions.includes(ext)) {
      return "image";
    }
    if (videoExtensions.includes(ext)) {
      return "video";
    }
    if (documentExtensions.includes(ext)) {
      return "document";
    }
  }

  return "unknown";
};

// Get file name from URL
export const getFileNameFromUrl = (src: string): string => {
  if (!src) return "File";

  try {
    const url = new URL(src, window.location.origin);
    const pathname = url.pathname;
    const segments = pathname.split("/");
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) {
      return decodeURIComponent(lastSegment);
    }
  } catch {
    // Try simple extraction
    const match = src.match(/[^/]+\.[a-zA-Z0-9]+(?:\?|$)/);
    if (match) {
      return decodeURIComponent(match[0].replace(/\?.*$/, ""));
    }
  }

  return "File";
};

export interface ResizableMediaOptions {
  HTMLAttributes: Record<string, unknown>;
  uploadFn?: (file: File) => Promise<string>;
  component?: React.ComponentType<any>;
  onMediaDelete?: (src: string) => void;
}

export const ResizableMedia = Node.create<ResizableMediaOptions>({
  name: "resizableMedia",

  addOptions() {
    return {
      HTMLAttributes: {},
      uploadFn: undefined,
    };
  },

  group: "block",

  draggable: true,

  atom: true,

  addAttributes() {
    // Helper: find nested img or video inside a wrapper div
    const getNestedMedia = (element: HTMLElement): HTMLElement | null =>
      element.querySelector("img") || element.querySelector("video");

    return {
      src: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("src");
          if (direct) return direct;
          return getNestedMedia(element)?.getAttribute("src") || null;
        },
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("alt");
          if (direct) return direct;
          return getNestedMedia(element)?.getAttribute("alt") || null;
        },
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("title");
          if (direct) return direct;
          return getNestedMedia(element)?.getAttribute("title") || null;
        },
        renderHTML: (attributes) => ({
          title: attributes.title,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element) =>
          element.getAttribute("width") || element.style.width || "100%",
        renderHTML: (attributes) => ({
          width: attributes.width,
        }),
      },
      height: {
        default: "auto",
        parseHTML: (element) => {
          const direct = element.getAttribute("height") || element.style.height;
          if (direct) return direct;
          const media = getNestedMedia(element);
          return media?.style.height || media?.getAttribute("height") || "auto";
        },
        renderHTML: (attributes) => ({
          height: attributes.height,
        }),
      },
      mediaType: {
        default: "image",
        parseHTML: (element) => {
          const dataType = element.getAttribute("data-media-type");
          if (dataType) return dataType;
          // Prioritize URL-based detection over element type, because legacy HTML
          // may wrap video/document URLs in an <img> tag
          const mediaSrc =
            element.getAttribute("src") ||
            getNestedMedia(element)?.getAttribute("src") ||
            "";
          const detected = detectMediaType(mediaSrc);
          if (detected !== "unknown") return detected;
          // Fallback to element type
          if (element.querySelector("video")) return "video";
          if (element.querySelector("img")) return "image";
          return "image";
        },
        renderHTML: (attributes) => ({
          "data-media-type": attributes.mediaType,
        }),
      },
      alignment: {
        default: "center",
        parseHTML: (element) => {
          const dataAttr = element.getAttribute("data-alignment");
          if (dataAttr) return dataAttr;
          // Fallback: extract from class name (e.g. "resizable-media-left")
          const className = element.getAttribute("class") || "";
          if (className.includes("resizable-media-left")) return "left";
          if (className.includes("resizable-media-right")) return "right";
          return "center";
        },
        renderHTML: (attributes) => ({
          "data-alignment": attributes.alignment,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="resizable-media"]',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const img = el.querySelector("img");
          const video = el.querySelector("video");
          const media = img || video;
          return {
            src: media?.getAttribute("src") || null,
            alt: media?.getAttribute("alt") || null,
            title: media?.getAttribute("title") || null,
            mediaType: el.getAttribute("data-media-type") || (() => {
              const mediaSrc = media?.getAttribute("src") || "";
              const detected = detectMediaType(mediaSrc);
              return detected !== "unknown" ? detected : (video ? "video" : "image");
            })(),
            alignment: el.getAttribute("data-alignment") || "center",
            width: el.getAttribute("width") || el.style.width || "100%",
            height: media?.style.height || media?.getAttribute("height") || "auto",
          };
        },
      },
      {
        tag: "img[src]",
        getAttrs: (element) => {
          const src = (element as HTMLElement).getAttribute("src");
          if (!src) return false;
          return {
            src,
            mediaType: "image",
          };
        },
      },
      {
        tag: "video[src]",
        getAttrs: (element) => {
          const src = (element as HTMLElement).getAttribute("src");
          if (!src) return false;
          return {
            src,
            mediaType: "video",
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, alt, title, width, height, mediaType, alignment } = HTMLAttributes;

    const wrapperAttrs = {
      "data-type": "resizable-media",
      "data-media-type": mediaType,
      "data-alignment": alignment,
      class: `resizable-media-wrapper resizable-media-${alignment}`,
      style: `width: ${typeof width === "number" ? `${width}px` : width}`,
    };

    if (mediaType === "video") {
      return [
        "div",
        wrapperAttrs,
        [
          "video",
          mergeAttributes(this.options.HTMLAttributes, {
            src,
            title,
            controls: true,
            style: `width: 100%; height: ${typeof height === "number" ? `${height}px` : height}`,
          }),
        ],
      ];
    }

    if (mediaType === "document") {
      const fileName = getFileNameFromUrl(src);
      const ext = fileName.split(".").pop()?.toLowerCase() || "";

      return [
        "div",
        mergeAttributes(wrapperAttrs, { class: `${wrapperAttrs.class} document-attachment` }),
        [
          "a",
          {
            href: src,
            target: "_blank",
            rel: "noopener noreferrer",
            class: "document-link",
          },
          [
            "span",
            { class: `document-icon document-icon-${ext}` },
          ],
          [
            "span",
            { class: "document-name" },
            fileName,
          ],
        ],
      ];
    }

    // Default: image
    return [
      "div",
      wrapperAttrs,
      [
        "img",
        mergeAttributes(this.options.HTMLAttributes, {
          src,
          alt,
          title,
          style: `width: 100%; height: ${typeof height === "number" ? `${height}px` : height}`,
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setMedia:
        (options) =>
        ({ commands }) => {
          const mediaType = options.mediaType || detectMediaType(options.src);
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
              mediaType: mediaType === "unknown" ? "image" : mediaType,
            },
          });
        },
    };
  },

  addNodeView() {
    // Dynamic import to avoid circular dependencies
    // The ResizableMediaComponent will be passed via configure()
    return ReactNodeViewRenderer(
      // This will be replaced by configure() with the actual component
      this.options.component || (() => null)
    );
  },

  addProseMirrorPlugins() {
    const onMediaDelete = this.options.onMediaDelete;
    const nodeTypeName = this.name;

    return [
      // Plugin to track media node deletions (backspace, delete, cut, etc.)
      ...(onMediaDelete
        ? [
            new Plugin({
              key: new PluginKey("resizableMediaDeletionTracker"),
              appendTransaction(transactions, oldState, newState) {
                // Only process if any transaction changed the doc
                const hasDocChange = transactions.some(tr => tr.docChanged);
                if (!hasDocChange) return null;

                // Skip internal media updates (e.g. blob→storage URL replacement)
                const isInternalMediaUpdate = transactions.some(tr => tr.getMeta("skipMediaDeleteTracking"));
                if (isInternalMediaUpdate) return null;

                // Collect all media src URLs in old and new documents
                const oldSrcs = new Set<string>();
                const newSrcs = new Set<string>();

                oldState.doc.descendants((node) => {
                  if (node.type.name === nodeTypeName && node.attrs.src) {
                    oldSrcs.add(node.attrs.src);
                  }
                });

                newState.doc.descendants((node) => {
                  if (node.type.name === nodeTypeName && node.attrs.src) {
                    newSrcs.add(node.attrs.src);
                  }
                });

                // Only call onMediaDelete for src URLs present in old but NOT in new
                for (const src of oldSrcs) {
                  if (!newSrcs.has(src)) {
                    onMediaDelete(src);
                  }
                }

                return null;
              },
            }),
          ]
        : []),
      new Plugin({
        key: new PluginKey("resizableMediaDropHandler"),
        props: {
          handleDrop: (view, event, _slice, moved) => {
            if (moved || !event.dataTransfer?.files?.length) {
              return false;
            }

            const file = event.dataTransfer.files[0];
            if (!file) return false;

            const ext = file.name.split(".").pop()?.toLowerCase() || "";
            const isImage = imageExtensions.includes(ext);
            const isVideo = videoExtensions.includes(ext);
            const isDocument = documentExtensions.includes(ext);

            if (!isImage && !isVideo && !isDocument) {
              return false;
            }

            event.preventDefault();

            const uploadFn = this.options.uploadFn;
            if (uploadFn) {
              uploadFn(file).then((src) => {
                const { tr } = view.state;
                const pos = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });

                if (pos) {
                  const mediaType = isImage ? "image" : isVideo ? "video" : "document";
                  const node = view.state.schema.nodes.resizableMedia.create({
                    src,
                    alt: file.name,
                    mediaType,
                  });
                  const transaction = tr.insert(pos.pos, node);
                  view.dispatch(transaction);
                }
              });
            } else {
              // Fallback to base64
              const reader = new FileReader();
              reader.onload = () => {
                const src = reader.result as string;
                const { tr } = view.state;
                const pos = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });

                if (pos) {
                  const mediaType = isImage ? "image" : isVideo ? "video" : "document";
                  const node = view.state.schema.nodes.resizableMedia.create({
                    src,
                    alt: file.name,
                    mediaType,
                  });
                  const transaction = tr.insert(pos.pos, node);
                  view.dispatch(transaction);
                }
              };
              reader.readAsDataURL(file);
            }

            return true;
          },
        },
      }),
    ];
  },
});

export default ResizableMedia;
